import React, { useState, useEffect, useRef, useCallback } from 'react';
import T from '../theme';

/* global ymaps */

// ========= NOMINATIM SEARCH =========
function GeoSearch({ onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=6&countrycodes=uz&accept-language=uz,ru,en`
      );
      const data = await r.json();
      setResults(data.map(d => ({
        display: d.display_name,
        short: d.display_name.split(',').slice(0, 2).join(', '),
        detail: d.display_name.split(',').slice(2, 4).join(', '),
        lat: parseFloat(d.lat),
        lng: parseFloat(d.lon),
      })));
      setOpen(data.length > 0);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(v), 500);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); clearTimeout(timer.current); search(query); }
  };

  const select = (r) => { setQuery(r.short); setOpen(false); onSelect(r); };

  return (
    <div ref={boxRef} style={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 1000 }}>
      <div style={{ position: 'relative', maxWidth: 420 }}>
        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 15, pointerEvents: 'none', opacity: 0.6 }}>🔍</span>
        <input
          value={query} onChange={handleInput} onKeyDown={handleKeyDown}
          placeholder="Manzil qidiring..."
          style={{
            width: '100%', padding: '12px 40px 12px 42px',
            background: 'rgba(255,255,255,0.97)', border: 'none',
            borderRadius: 12, fontSize: 14, fontFamily: T.font,
            fontWeight: 600, color: T.text, outline: 'none',
            boxSizing: 'border-box',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}
          onFocus={() => { if (results.length) setOpen(true); }}
        />
        {loading && <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: T.blue }}>⟳</span>}
        {query && !loading && (
          <button onClick={() => { setQuery(''); setResults([]); setOpen(false); }} style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: T.textMuted, padding: 4,
          }}>✕</button>
        )}
      </div>

      {open && results.length > 0 && (
        <div style={{
          maxWidth: 420, marginTop: 6, background: '#fff', borderRadius: 14,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)', overflow: 'hidden',
          maxHeight: 280, overflowY: 'auto',
        }}>
          {results.map((r, i) => (
            <button key={i} onMouseDown={() => select(r)} style={{
              width: '100%', padding: '12px 16px', background: 'none', border: 'none',
              borderBottom: i < results.length - 1 ? `1px solid ${T.border}` : 'none',
              cursor: 'pointer', fontFamily: T.font, textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: 10,
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#F0FDF4'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>📍</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.short}</div>
                {r.detail && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.detail}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ========= REVERSE GEOCODE (Nominatim) =========
async function reverseGeocode(lat, lng) {
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=uz,ru,en&zoom=18`
    );
    const d = await r.json();
    return d.display_name || '';
  } catch { return ''; }
}

// ========= YANDEX MAP COMPONENT =========
export default function MapPicker({
  latitude, longitude, radius = 150,
  color = '#10B981', icon = '📍',
  onChange, onRadiusChange,
  readOnly = false, height = 420,
  otherZones = [],
  showSearch = true,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const otherObjectsRef = useRef([]);
  const [ready, setReady] = useState(false);
  // Ichki o'zgarish flagı — click/drag dan keyin setCenter chaqirmaslik uchun
  const internalChangeRef = useRef(false);

  const defaultCenter = [39.77, 64.42];

  useEffect(() => {
    if (typeof ymaps === 'undefined') return;
    ymaps.ready(() => setReady(true));
  }, []);

  // Map yaratish — faqat 1 marta
  useEffect(() => {
    if (!ready || !mapRef.current || mapInstanceRef.current) return;

    const center = latitude && longitude ? [latitude, longitude] : defaultCenter;
    const map = new ymaps.Map(mapRef.current, {
      center,
      zoom: latitude && longitude ? 16 : 13,
      controls: ['zoomControl', 'geolocationControl'],
    }, { suppressMapOpenBlock: true });

    mapInstanceRef.current = map;

    if (!readOnly) {
      map.events.add('click', async (e) => {
        const coords = e.get('coords');
        // Marker va circle ni yangilash (xaritani zoom QILMASLIK)
        placeMarker(coords, map);
        internalChangeRef.current = true;
        const addr = await reverseGeocode(coords[0], coords[1]);
        onChange?.({
          latitude: Math.round(coords[0] * 1000000) / 1000000,
          longitude: Math.round(coords[1] * 1000000) / 1000000,
          address: addr,
        });
      });
    }

    if (latitude && longitude) placeMarker([latitude, longitude], map);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
      }
    };
  }, [ready]);

  // Tashqaridan props o'zgarganda — faqat tashqi o'zgarishda center qilish
  useEffect(() => {
    if (!mapInstanceRef.current || !latitude || !longitude) return;
    // Agar ichki o'zgarish bo'lsa — faqat marker/circle yangilash, zoom QILMASLIK
    if (internalChangeRef.current) {
      internalChangeRef.current = false;
      placeMarker([latitude, longitude], mapInstanceRef.current);
      return;
    }
    // Tashqi o'zgarish (masalan: tahrirlashda ochilganda) — center qilish
    placeMarker([latitude, longitude], mapInstanceRef.current);
    mapInstanceRef.current.setCenter([latitude, longitude], 16, { duration: 500 });
  }, [latitude, longitude]);

  // Radius o'zgarganda
  useEffect(() => {
    if (circleRef.current) circleRef.current.geometry.setRadius(radius);
  }, [radius]);

  // Boshqa zonalar
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    otherObjectsRef.current.forEach(obj => map.geoObjects.remove(obj));
    otherObjectsRef.current = [];
    otherZones.filter(z => z.latitude && z.longitude).forEach(z => {
      const zColor = z.color || '#94A3B8';
      const pm = new ymaps.Placemark([z.latitude, z.longitude], {
        balloonContent: `<b>${z.icon || '📍'} ${z.name}</b><br>${z.address || ''}<br>Radius: ${z.radius}m`,
      }, { preset: 'islands#dotIcon', iconColor: zColor });
      map.geoObjects.add(pm);
      otherObjectsRef.current.push(pm);
      const c = new ymaps.Circle([[z.latitude, z.longitude], z.radius || 150], {}, {
        fillColor: zColor + '15', strokeColor: zColor, strokeWidth: 1.5, strokeStyle: 'dash', fillOpacity: 0.1,
      });
      map.geoObjects.add(c);
      otherObjectsRef.current.push(c);
    });
  }, [otherZones, ready]);

  // Marker va circle qo'yish/yangilash
  const placeMarker = (coords, map) => {
    // Marker
    if (markerRef.current) {
      markerRef.current.geometry.setCoordinates(coords);
    } else {
      const pm = new ymaps.Placemark(coords, {}, {
        iconLayout: 'default#imageWithContent',
        iconImageHref: '', iconImageSize: [42, 42], iconImageOffset: [-21, -42],
        iconContentLayout: ymaps.templateLayoutFactory.createClass(
          `<div style="width:42px;height:42px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 4px 14px ${color}60;display:flex;align-items:center;justify-content:center;font-size:19px;line-height:1;">${icon}</div>`
        ),
        draggable: !readOnly,
      });
      if (!readOnly) {
        pm.events.add('dragend', async () => {
          const nc = pm.geometry.getCoordinates();
          // Circle ham sudralgan joyga ko'chsin
          if (circleRef.current) {
            circleRef.current.geometry.setCoordinates(nc);
          }
          internalChangeRef.current = true;
          const addr = await reverseGeocode(nc[0], nc[1]);
          onChange?.({
            latitude: Math.round(nc[0] * 1000000) / 1000000,
            longitude: Math.round(nc[1] * 1000000) / 1000000,
            address: addr,
          });
        });
      }
      map.geoObjects.add(pm);
      markerRef.current = pm;
    }

    // Circle (radius)
    if (onRadiusChange || radius > 0) {
      if (circleRef.current) {
        circleRef.current.geometry.setCoordinates(coords);
        circleRef.current.geometry.setRadius(radius);
      } else {
        const c = new ymaps.Circle([coords, radius], {}, {
          fillColor: color + '18', strokeColor: color, strokeWidth: 2.5, strokeStyle: 'dash', fillOpacity: 0.12,
        });
        map.geoObjects.add(c);
        circleRef.current = c;
      }
    }
  };

  // Qidiruv natijasi — bu yerda zoom QILAMIZ
  const handleSearchSelect = async (result) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const coords = [result.lat, result.lng];
    placeMarker(coords, map);
    map.setCenter(coords, 17, { duration: 500 });
    internalChangeRef.current = true;
    onChange?.({ latitude: result.lat, longitude: result.lng, address: result.display.split(',').slice(0, 3).join(', ') });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        borderRadius: 18, overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: `2px solid ${T.border}`, position: 'relative',
      }}>
        <div ref={mapRef} style={{ height, width: '100%' }} />
        {!readOnly && showSearch && <GeoSearch onSelect={handleSearchSelect} />}
        {!readOnly && !latitude && !longitude && (
          <div style={{
            position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(15,29,21,0.85)', color: '#fff', padding: '11px 22px',
            borderRadius: 12, fontSize: 14, fontWeight: 700, fontFamily: T.font,
            zIndex: 800, whiteSpace: 'nowrap', pointerEvents: 'none',
          }}>🖱 Xaritani bosib joy tanlang yoki qidiring</div>
        )}
        {latitude && longitude && (
          <div style={{
            position: 'absolute', bottom: 12, left: 12,
            background: 'rgba(255,255,255,0.96)', padding: '8px 14px',
            borderRadius: 10, fontSize: 12, fontWeight: 700, fontFamily: T.mono,
            color: T.text, zIndex: 800,
          }}>📍 {latitude.toFixed ? latitude.toFixed(6) : latitude}, {longitude.toFixed ? longitude.toFixed(6) : longitude}</div>
        )}
      </div>
      {!readOnly && onRadiusChange && (
        <div style={{ background: '#fff', borderRadius: 16, padding: '18px 22px', boxShadow: T.shadow, border: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>⭕</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: T.textMuted, letterSpacing: 0.5 }}>GEOFENCE RADIUS</span>
            </div>
            <span style={{ fontSize: 22, fontWeight: 900, fontFamily: T.mono, color: color, background: color + '12', padding: '5px 16px', borderRadius: 12 }}>{radius}m</span>
          </div>
          <input type="range" min="30" max="500" value={radius}
            onChange={(e) => onRadiusChange(parseInt(e.target.value))}
            style={{ width: '100%', accentColor: color, height: 6, cursor: 'pointer' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.textMuted, marginTop: 8, fontWeight: 700 }}>
            <span>30m</span><span>100m</span><span>200m</span><span>350m</span><span>500m</span>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            {[{ label: '🏫 Maktab', v: 120 }, { label: '🏠 Uy', v: 80 }, { label: '🌸 Boqcha', v: 100 }, { label: '⚽ Sport', v: 200 }].map(p => (
              <button key={p.v} onClick={() => onRadiusChange(p.v)} style={{
                flex: 1, padding: '7px 4px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                background: radius === p.v ? color + '15' : T.bg,
                border: `1.5px solid ${radius === p.v ? color : T.border}`,
                color: radius === p.v ? color : T.textMuted, cursor: 'pointer', fontFamily: T.font,
              }}>{p.label}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function createIcon() { return null; }
